// The owner's POS pages for one outlet (slice 51a, ADR 0046): one page, one
// tab per question an owner asks — what did we sell, what needs a look, is
// the drawer right, did the customer pay, which refund is waiting for me.
//
// Everything here is a read of what the server already derived, except what an
// owner or manager does: decide a refund, close the day, work an exception,
// set up or end a till (slice 51b). Tab choice lives in the URL (`?tab=`) so a reload or a
// shared link lands on the same view. Permissions only decide which controls
// are drawn; the server enforces every one of them.

import type { JSX } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../api/client.js';
import type { MeResponse } from '../api/types.js';
import { useAuth } from '../auth/AuthContext.js';
import { hasPermission, useOutletPermissions } from '../auth/permissions.js';
import { BillsTab } from './pos/BillsTab.js';
import { DevicesTab } from './pos/DevicesTab.js';
import { ExceptionsTab } from './pos/ExceptionsTab.js';
import { PaymentsTab } from './pos/PaymentsTab.js';
import { RefundsTab } from './pos/RefundsTab.js';
import { SalesTab } from './pos/SalesTab.js';
import { SeriesTab } from './pos/SeriesTab.js';
import { StaffTab } from './pos/StaffTab.js';
import { ShiftsTab } from './pos/ShiftsTab.js';

export const POS_TABS = [
  { id: 'sales', label: 'Sales' },
  { id: 'bills', label: 'Bills' },
  { id: 'shifts', label: 'Shifts & cash' },
  { id: 'payments', label: 'UPI payments' },
  { id: 'refunds', label: 'Refunds' },
  { id: 'exceptions', label: 'Needs a look' },
  { id: 'devices', label: 'Devices' },
  { id: 'staff', label: 'Staff' },
  { id: 'series', label: 'Invoice numbers' },
] as const;
export type PosTabId = (typeof POS_TABS)[number]['id'];

function isTab(v: string | null): v is PosTabId {
  return POS_TABS.some((t) => t.id === v);
}

export function PosPage(): JSX.Element {
  const { orgId, outletId } = useParams<{ orgId: string; outletId: string }>();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const requested = params.get('tab');
  const tab: PosTabId = isTab(requested) ? requested : 'sales';

  const me = useQuery({
    queryKey: ['me'],
    queryFn: () => apiFetch<MeResponse>('/me'),
    staleTime: 30_000,
  });
  const permissions = useOutletPermissions(orgId, outletId);
  const canRead = hasPermission(permissions, 'pos:read');
  const canManage = hasPermission(permissions, 'integration:manage');
  const can = (p: Parameters<typeof hasPermission>[1]): boolean => hasPermission(permissions, p);
  const restaurantId = me.data?.memberships.find(
    (m) => m.organization.id === orgId && m.outlet.id === outletId,
  )?.restaurant.id;

  if (!orgId || !outletId) return <main className="page" />;

  return (
    <main className="page">
      <header className="page-header">
        <div>
          <h1>Till &amp; sales</h1>
          <p className="muted">
            Signed in as <strong>{user?.email}</strong> · <Link to="/home">Your outlets</Link>
            {restaurantId && canManage ? (
              <>
                {' '}
                ·{' '}
                <Link to={`/orgs/${orgId}/restaurants/${restaurantId}/pos/settlements`}>
                  Settlement reports
                </Link>
              </>
            ) : null}
          </p>
        </div>
      </header>

      {me.isSuccess && !canRead ? (
        <p className="error" role="alert">
          You do not have permission to see the till and sales pages.
        </p>
      ) : null}

      {canRead ? (
        <>
          <nav className="pos-tabs" aria-label="Till and sales sections">
            {POS_TABS.map((t) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={tab === t.id}
                className={`pos-tab${tab === t.id ? ' pos-tab-active' : ''}`}
                onClick={() => setParams({ tab: t.id }, { replace: true })}
              >
                {t.label}
              </button>
            ))}
          </nav>
          <div role="tabpanel">
            {tab === 'sales' ? (
              <SalesTab
                orgId={orgId}
                outletId={outletId}
                canClose={can('pos:period_close')}
                canReopen={can('pos:period_reopen')}
              />
            ) : null}
            {tab === 'bills' ? <BillsTab orgId={orgId} outletId={outletId} /> : null}
            {tab === 'shifts' ? <ShiftsTab orgId={orgId} outletId={outletId} /> : null}
            {tab === 'payments' ? <PaymentsTab orgId={orgId} outletId={outletId} /> : null}
            {tab === 'refunds' ? (
              <RefundsTab orgId={orgId} outletId={outletId} canDecide={canManage} />
            ) : null}
            {tab === 'exceptions' ? (
              <ExceptionsTab
                orgId={orgId}
                outletId={outletId}
                canWork={can('pos:exception_manage')}
                canReplay={can('pos:event_replay')}
              />
            ) : null}
            {tab === 'devices' ? (
              <DevicesTab
                orgId={orgId}
                outletId={outletId}
                restaurantId={restaurantId}
                canManage={can('pos:device_manage')}
                canEnable={canManage}
              />
            ) : null}
            {tab === 'staff' ? (
              <StaffTab orgId={orgId} outletId={outletId} canRemove={can('pos:staff_manage')} />
            ) : null}
            {tab === 'series' ? <SeriesTab orgId={orgId} outletId={outletId} /> : null}
          </div>
        </>
      ) : null}
    </main>
  );
}
